import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { botVoteApi, BotVoteStatus } from '../../api/admin.api';

/**
 * S0-28 어드민 봇 투표 모달
 *
 * 🔴 임시 조치 — 앱 궤도 진입 시 폐기 예정. 제거 시 이 파일과
 *    SurveyPinManagement 의 `봇투표` 컬럼, admin.api.ts 의 botVoteApi 를 함께 지운다.
 *
 * 흐름: 선택지 고르기 → 페르소나 고르기 → 투표.
 * 일괄 투표 버튼은 의도적으로 만들지 않는다 — 10표가 동일 시각에 INSERT 되면
 * 투표 시각 분포로 조작 패턴이 그대로 드러난다(spec §2).
 */
interface Props {
  questionId: number;
  onClose: () => void;
}

export const BotVoteModal: React.FC<Props> = ({ questionId, onClose }) => {
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const {
    data: status,
    isLoading,
    error,
  } = useQuery<BotVoteStatus>({
    queryKey: ['botVoteStatus', questionId],
    queryFn: async () => (await botVoteApi.getVoteStatus(questionId)).data,
  });

  const voteMutation = useMutation({
    mutationFn: (botUserId: number) => {
      if (selectedOptionId === null) {
        return Promise.reject(new Error('선택지를 먼저 골라주세요.'));
      }
      return botVoteApi.vote(questionId, selectedOptionId, botUserId);
    },
    onSuccess: (res) => {
      setErrorMessage(null);
      // 응답이 갱신된 현황이므로 재조회 없이 캐시를 교체한다.
      queryClient.setQueryData(['botVoteStatus', questionId], res.data);
      // 뒤 목록의 투표수도 갱신되어야 한다.
      queryClient.invalidateQueries({ queryKey: ['adminSurveyList'] });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error)?.message ??
        '투표에 실패했어요.';
      setErrorMessage(message);
    },
  });

  const votedCount = status?.personas.filter((p) => p.voted).length ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-[#E5E7EB]">
          <div className="pr-4">
            <h3 className="text-base font-bold text-[#030213]">봇 투표</h3>
            <p className="text-sm text-[#6B7280] mt-0.5 line-clamp-2">{status?.description ?? '불러오는 중...'}</p>
          </div>
          <button
            onClick={onClose}
            className="text-[#9CA3AF] hover:text-[#6B7280] text-lg leading-none"
            aria-label="닫기"
          >
            ✕
          </button>
        </div>

        {isLoading && <div className="p-6 text-center text-sm text-[#6B7280]">불러오는 중...</div>}
        {error && <div className="p-6 text-center text-sm text-red-500">투표 현황을 불러오지 못했습니다.</div>}

        {status && (
          <div className="p-5 space-y-5">
            {status.closed && (
              <div className="rounded-lg bg-[#F3F4F6] px-3 py-2 text-xs text-[#6B7280]">
                종료된 설문이라 투표할 수 없어요.
              </div>
            )}

            {/* STEP 1 — 선택지 */}
            <section>
              <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-2">
                1. 선택지 ({status.totalVotes ?? 0}표)
              </h4>
              <div className="space-y-1.5">
                {status.options.map((option) => (
                  <button
                    key={option.optionId}
                    onClick={() => setSelectedOptionId(option.optionId)}
                    disabled={status.closed}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border text-sm transition-colors disabled:opacity-50 ${
                      selectedOptionId === option.optionId
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-[#E5E7EB] hover:bg-[#f3f3f5] text-[#030213]'
                    }`}
                  >
                    <span className="text-left">{option.optionText}</span>
                    <span className="text-xs text-[#6B7280] ml-3 shrink-0">{option.voteCount ?? 0}표</span>
                  </button>
                ))}
              </div>
            </section>

            {/* STEP 2 — 페르소나 */}
            <section>
              <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-2">
                2. 페르소나 ({votedCount}/{status.personas.length} 투표함)
              </h4>

              {selectedOptionId === null ? (
                <p className="text-xs text-[#9CA3AF] px-1">선택지를 먼저 고르면 목록이 활성화돼요.</p>
              ) : null}

              <div className="space-y-1.5 mt-1.5">
                {status.personas.map((persona) => {
                  const votedOption = status.options.find((o) => o.optionId === persona.votedOptionId);
                  const disabled =
                    persona.voted || status.closed || selectedOptionId === null || voteMutation.isPending;

                  return (
                    <div
                      key={persona.userId}
                      className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#f3f3f5]"
                    >
                      <div className="min-w-0">
                        <span className="text-sm text-[#030213]">{persona.label}</span>
                        <span className="text-xs text-[#9CA3AF] ml-2">{persona.nickname}</span>
                      </div>

                      {persona.voted ? (
                        <span className="text-xs text-[#6B7280] ml-3 shrink-0">
                          ✓ {votedOption?.optionText ?? '투표함'}
                        </span>
                      ) : (
                        <button
                          onClick={() => voteMutation.mutate(persona.userId)}
                          disabled={disabled}
                          className="ml-3 shrink-0 px-3 py-1 text-xs font-medium rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-40 disabled:hover:bg-blue-50"
                        >
                          투표
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {errorMessage && <p className="text-xs text-red-500 px-1">{errorMessage}</p>}
          </div>
        )}
      </div>
    </div>
  );
};
